import dynamic from 'next/dynamic';
import { ChangeEvent, useRef, useState } from 'react';
import { handleTrack } from '../../../../utils/tracker';
import { Button, ButtonVariants } from '../../../Atoms/Button';
import { If } from '../../../Atoms/Conditionals';
import { MarktechModal } from '../../../Atoms/Modal';
import { CloseIcon } from '../../../Icons';
import { useCommunityQACtx } from './community-context/comment-context';
import { Disclaimer } from './disclaimer';
import loader from './lottie/loader.json';
import CommunityQAServices from './services/community-qa.service';
import { UserNameModal } from './username-modal';
import { CommentTemplate, titleList } from './utils/constant';
import {
  ActionTypes,
  ICommentContent,
  IParentProps,
  IPropsCommentBox,
  StatusType,
} from './utils/types';
import { resizeBox } from './utils/utils';

const Lottie = dynamic(() => import('lottie-react'), {
  ssr: false,
});

export const CreateCommentBox = ({
  parentId = null,
  type = ActionTypes.CreateParentComment,
  isParentComment = false,
  content = {
    content: '',
    reported: false,
    last_edited_on: null,
  },
  commentId = -1,
  setCommentContent = () => {},
  setDisplayActionModal = () => {},
  setReplies = () => {},
  replies,
}: IPropsCommentBox) => {
  const [userModal, setUserModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [moderationError, setModerationError] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [typeComment, setTypeComment] = useState(content.content);
  const { postId, setParentComment, parentComment, currentUserName } =
    useCommunityQACtx();

  const handleClear = (e: React.MouseEvent | React.TouchEvent) => {
    setTypeComment('');
    handleTrack({ label: 'communtiy_comment', obj: e.target });
  };

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    if (currentUserName !== null) {
      resizeBox(textAreaRef);
      setTypeComment(e.target.value);
    }
  };

  function createCommentUtil(
    response: {
      data: ICommentContent;
      status: string;
    },
    comment: IParentProps | undefined,
    setComment: (value: IParentProps) => void
  ) {
    const mergeCommentData = {
      ...CommentTemplate.results[0],
      ...response.data,
    };

    if (response.status === StatusType.Success && comment) {
      if (type === ActionTypes.CreateParentComment) {
        setComment({
          ...comment,
          results: [mergeCommentData, ...comment.results],
        });
      } else if (type === ActionTypes.CreateRepliesComment) {
        setComment({
          ...comment,
          results: [...comment.results, mergeCommentData],
        });
      }
    } else if (response.status === StatusType.Derogatory) {
      setModerationError(true);
    } else {
      setModerationError(true);
    }
    setIsLoading(false);
  }

  const handleComment = async (e: React.MouseEvent | React.TouchEvent) => {
    setIsLoading(true);
    setModerationError(false);
    const createPayload = {
      content: typeComment,
      parent: parentId,
      post: postId,
    };
    if (type === ActionTypes.CreateParentComment && typeComment) {
      CommunityQAServices.createComment(createPayload).then((response) => {
        createCommentUtil(response, parentComment, setParentComment);
      });
    } else if (type === ActionTypes.EditComment) {
      setDisplayActionModal(true);
      const payload = {
        content: typeComment,
      };

      CommunityQAServices.editComment(payload, commentId).then((response) => {
        setIsLoading(true);
        if (response.status === StatusType.Success) {
          setModerationError(false);
          setDisplayActionModal(false);
          setCommentContent({
            ...content,
            content: typeComment,
            last_edited_on: '2024-04-19T09:07:47.631650+05:30',
          });
        } else if (response.status === StatusType.Derogatory) {
          setModerationError(true);
        } else {
          setModerationError(true);
        }
        setIsLoading(false);
      });
    } else if (type === ActionTypes.CreateRepliesComment) {
      CommunityQAServices.createComment(createPayload).then((response) => {
        createCommentUtil(response, replies, setReplies);
      });
    }

    handleClear(e);
  };

  const handleUserName = () => {
    if (currentUserName) {
      return;
    }
    setUserModal(true);
  };

  return (
    <>
      <If condition={!!titleList[type as keyof typeof titleList]}>
        <h2 className="testing123 text-s-20 font-semibold mb-6">
          {titleList[type as keyof typeof titleList]}
        </h2>
      </If>
      <div className="relative">
        <div
          className={`p-6 rounded-md border border-blue-350 outline-none mb-4 ${
            isLoading ? 'opacity-0' : 'opacity-1'
          }`}
          onClick={handleUserName}
        >
          <div className="flex gap-3 items-center mb-6">
            <img src="https://assets.clear.in/cleartax/images/1709197329_rectangle2521069.svg" />
            <h3 className="text-base font-bold">
              {currentUserName ? currentUserName : 'User'}
            </h3>
          </div>
          <textarea
            style={{ height: '48px' }}
            ref={textAreaRef}
            data-parent-comment={isParentComment}
            data-parent-id={isParentComment ? null : parentId}
            placeholder="What are your thoughts"
            className="outline-none mb-4 w-full resize-none min-h-[48px] max-h-[180px] overflow-y-auto"
            onChange={handleChange}
            value={typeComment}
          ></textarea>
          <div className="text-end">
            <Button
              variant={ButtonVariants.bluetext}
              rounded="md"
              onClick={handleClear}
              className={`${
                typeComment ? '' : 'opacity-60 pointer-events-none'
              }`}
            >
              Clear
            </Button>
            <Button
              variant={ButtonVariants.bluefilled}
              rounded="md"
              className={`py-3.5 w-[100px] ml-3 ${
                typeComment ? '' : 'opacity-60  pointer-events-none'
              }`}
              onClick={handleComment}
            >
              Submit
            </Button>
          </div>
        </div>
        <div
          className={`p-6 rounded-md border border-blue-350 outline-none mb-4 absolute top-0 w-full h-full flex flex-col items-center ${
            isLoading ? 'block' : 'hidden'
          }`}
        >
          <Lottie animationData={loader} loop={true} className="h-32 w-32" />
          <div>Please wait while we verify your comment</div>
        </div>
        {moderationError ? (
          <Disclaimer
            icon="https://assets.clear.in/cleartax/images/1713436905_info2.svg"
            text="This comment is not allowed. Please make sure your comments are not derogatory."
            textColor="red"
          />
        ) : (
          ''
        )}
      </div>
      <Disclaimer
        icon="https://assets.clear.in/cleartax/images/1713436660_info1.svg"
        text="We value user comments but do not assume responsibility for any
        derogatory remarks as we aim to ensure a positive user experience."
      />
      <MarktechModal
        open={userModal}
        onClose={() => console.log('username modal')}
        classNames={{
          modal: 'max-w-[800px] sm:max-w-[320px] mobile-search-box rounded-2xl',
        }}
        closeIcon={<CloseIcon setDisplayActionModal={setUserModal} />}
        center
      >
        <UserNameModal setUserModal={setUserModal} />
      </MarktechModal>
    </>
  );
};
